import { CalendarClock, ChevronRight, Target, TrendingUp } from 'lucide-react';
import { useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import type { DashboardSnapshot } from './dashboard.types';
import { formatLongDate, formatMoney, formatShortDate } from './dashboard-format';
import { ProjectionChart } from './ProjectionChart';

export function HomeView({ snapshot }: { readonly snapshot: DashboardSnapshot }) {
  const { displayProjection, healthTarget, nextMovement, projectionStatus } = snapshot;
  const { locale, t } = useI18n();
  const [futureHorizon, setFutureHorizon] = useState<'month' | 'three' | 'six'>('three');
  const projectionLabel = t(
    projectionStatus.status === 'critical'
      ? 'dashboard.projectionCritical'
      : projectionStatus.status === 'risk'
        ? 'dashboard.projectionRisk'
        : 'dashboard.projectionStable',
  );
  const monthPoints = displayProjection.points.filter(
    (point) => point.date <= displayProjection.endMonthDate,
  );
  const selectedFuture = {
    month: {
      balance: displayProjection.endMonthBalance,
      date: displayProjection.endMonthDate,
      label: t('dashboard.endMonth'),
    },
    six: {
      balance: displayProjection.sixMonthBalance,
      date: displayProjection.sixMonthDate,
      label: t('dashboard.inSixMonths'),
    },
    three: {
      balance: displayProjection.threeMonthBalance,
      date: displayProjection.threeMonthDate,
      label: t('dashboard.inThreeMonths'),
    },
  }[futureHorizon];

  return (
    <>
      <section className="projection-card" aria-labelledby="projection-title">
        <div className="projection-card-heading">
          <div>
            <h1 id="projection-title">{t('dashboard.ifNothingChanges')}</h1>
            <p className="projection-amount">
              {formatMoney(displayProjection.endMonthBalance, locale)}
            </p>
            <p className="projection-date">
              {t('dashboard.projectedBy', {
                date: formatLongDate(displayProjection.endMonthDate, locale),
              })}
            </p>
          </div>
          <span className={`status-badge status-${projectionStatus.status}`}>
            <i aria-hidden="true" />
            {projectionLabel}
          </span>
        </div>

        <ProjectionChart points={monthPoints} />

        <dl className="projection-stats">
          <div>
            <dt>{t('dashboard.today')}</dt>
            <dd>{formatMoney(displayProjection.todayBalance, locale)}</dd>
          </div>
          <div>
            <dt>{t('dashboard.endMonth')}</dt>
            <dd>{formatMoney(displayProjection.endMonthBalance, locale)}</dd>
          </div>
          <div>
            <dt>{t('dashboard.lowPoint')}</dt>
            <dd>
              {formatMoney(displayProjection.lowPoint.balance, locale)}
              <small>{formatShortDate(displayProjection.lowPoint.date, locale)}</small>
            </dd>
          </div>
        </dl>
        <a className="projection-action" href="#plan">
          {t('dashboard.exploreFuture')} <ChevronRight aria-hidden="true" />
        </a>
      </section>

      <section className="future-card" aria-labelledby="future-title">
        <header>
          <div>
            <p className="section-kicker">{t('dashboard.futureEyebrow')}</p>
            <h2 id="future-title">{t('dashboard.futureTitle')}</h2>
          </div>
          <TrendingUp className="future-icon" aria-hidden="true" />
        </header>
        <div className="future-horizon-tabs" aria-label={t('dashboard.chooseFutureDate')}>
          {(['month', 'three', 'six'] as const).map((horizon) => {
            const label =
              horizon === 'month'
                ? t('dashboard.oneMonth')
                : horizon === 'three'
                  ? t('dashboard.inThreeMonths')
                  : t('dashboard.inSixMonths');
            return (
              <button
                aria-pressed={futureHorizon === horizon}
                className={futureHorizon === horizon ? 'is-active' : undefined}
                key={horizon}
                type="button"
                onClick={() => setFutureHorizon(horizon)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="future-selected-value" aria-live="polite">
          <span>{selectedFuture.label}</span>
          <strong>{formatMoney(selectedFuture.balance, locale)}</strong>
          <small>{formatLongDate(selectedFuture.date, locale)}</small>
        </div>
      </section>

      <div className="home-support-grid">
        <a className="dashboard-card next-event-card" href="#calendario">
          <span className="card-icon card-icon-accent">
            <CalendarClock aria-hidden="true" />
          </span>
          <div>
            <p className="section-kicker">{t('dashboard.nextMovement')}</p>
            {nextMovement === null ? (
              <>
                <h2>{t('dashboard.agendaClear')}</h2>
                <p>{t('dashboard.noProjection')}</p>
              </>
            ) : (
              <>
                <h2>{nextMovement.label}</h2>
                <p>{formatLongDate(nextMovement.date, locale)}</p>
              </>
            )}
          </div>
          {nextMovement !== null && (
            <strong className={`movement-value movement-${nextMovement.type}`}>
              {nextMovement.net >= 0 && nextMovement.type !== 'transfer' ? '+' : ''}
              {formatMoney(nextMovement.amount, locale)}
            </strong>
          )}
          <ChevronRight className="row-chevron" aria-hidden="true" />
        </a>

        <a className={`dashboard-card target-card target-${healthTarget.status}`} href="#plan">
          <span className="card-icon card-icon-income">
            <Target aria-hidden="true" />
          </span>
          <div>
            <p className="section-kicker">{t('dashboard.dailyGoal')}</p>
            <p>
              {healthTarget.status === 'covered'
                ? t('dashboard.covered')
                : t('dashboard.remainingGoal', {
                    amount: formatMoney(healthTarget.deficit, locale),
                    days: healthTarget.remainingDays,
                  })}
            </p>
          </div>
          <strong className="target-value">
            {formatMoney(healthTarget.requiredToday, locale)}
          </strong>
          <ChevronRight className="row-chevron" aria-hidden="true" />
        </a>
      </div>
    </>
  );
}
